import React from "react";
import { TransitionProps } from "@mui/material/transitions";
import Slide from "@mui/material/Slide";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Button from "../../common/Button";
import { Cancel, DownloadOutlined } from "@mui/icons-material";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchDocumentById } from "../../../redux/slice/practiceSlice";
import { setSingleAlertObj } from "../../../redux/slice/commonSlice";
import CustomIconButton from "../../common/CustomIconButton";

type VersionHistoryItem = {
  name: string;
  date: string;
  type: string;
  docId?: string;
  description?: string;
  status?: string;
};

const historyData: VersionHistoryItem[] = [
  {
    name: "Legacy Document 2025",
    date: "March 14, 2025",
    type: "PDF",
    description: "Initial Version",
    status: "New",
  },
  {
    name: "Financial Report Q1",
    date: "March 02, 2025",
    type: "Excel",
    description: "Updated financial figures",
    status: "Delete",
  },
  {
    name: "Project Proposal 2025",
    date: "March 10, 2025",
    type: "Word",
    description: "Reviewed and approved proposal document",
    status: "Delete",
  },
];

interface DocumentHistoryPopupProps {
  open: boolean;
  documentData: any;
  onClose: () => void;
  title?: string;
  downloadFile?: boolean;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DocumentHistoryPopup: React.FC<DocumentHistoryPopupProps> = ({
  open,
  documentData,
  onClose,
  title,
  downloadFile = false,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const downloadBase64File = (
    base64String: string,
    fileName: string,
    mimeType: string
  ) => {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor); // Required for Firefox
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
  };

  const handleActionDownload = async (docId: string) => {
    if (docId) {
      const actionResult = await dispatch(fetchDocumentById(docId.toString()));
      const documentData = actionResult.payload;
      if (documentData?.content) {
        downloadBase64File(documentData.content, documentData.documentName, documentData.fileType);
      } else {
        dispatch(setSingleAlertObj({ message: "Document not found!", alertType: "info" }));
      }
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      aria-describedby="document-history-dialog"
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          background:
            theme.palette.mode === "dark"
              ? "rgba(30, 30, 30, 0.8)"
              : "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
        },
      }}
    >
      <DialogTitle>
        {title ? <b>{title}</b> : <b>Document History</b>}
      </DialogTitle>

      <DialogContent onClick={(event) => event.stopPropagation()}>
        <Box
          sx={{
            border: 1,
            p: 2,
            borderRadius: 3,
            borderColor: theme.palette.grey[600],
          }}
        >
          <Stack spacing={3} divider={<Divider flexItem />}>
            {documentData.map((item: any, index: any) => (
              <Stack
                key={index}
                direction="row"
                alignItems="center"
                spacing={2}
              >
                <Stack alignItems="center">
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor:
                        item.status === "Active"
                          ? "green"
                          : item.status === "Deleted"
                          ? "red"
                          : "orange",
                    }}
                  />
                  {index !== documentData.length - 1 && (
                    <Box
                      sx={{
                        width: 2,
                        flexGrow: 1,
                        bgcolor: "grey.400",
                        mt: 0.5,
                      }}
                    />
                  )}
                </Stack>

                {/* Document Info */}
                <Stack spacing={1} sx={{ flex: 1 }}>
                  <Typography fontWeight="bold" fontSize="1rem">
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.type} • {item.date}
                  </Typography>
                  {item.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          theme.palette.mode === "dark"
                            ? "grey.500"
                            : "grey.700",
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                </Stack>

                {/* Download Button */}
                {downloadFile === true && (
                  <CustomIconButton
                    color="info"
                    size="small"
                    variant="outlined"
                    onClick={() => handleActionDownload(item.docId)}
                    tooltipMessage="Download"
                    tooltipPlacement="bottom"
                  >
                    <DownloadOutlined />
                  </CustomIconButton>
                )}

                {/* Status Chip */}
                {/* <Chip
                  label={item.status}
                  sx={{
                    minWidth: "60px",
                    backgroundColor:
                      item.status === "Active"
                        ? "rgba(76, 175, 80, 0.2)"
                        : "rgba(255, 0, 0, 0.2)",
                    color:
                      item.status === "Active"
                        ? "green"
                        : item.status === "Inactive"
                        ? "orange"
                        : "red",
                    fontWeight: "bold",
                  }}
                /> */}
              </Stack>
            ))}
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end", p: 2 }}>
        {/* CS-1404 - Removing the start icon and close button will be similar as pdf viewer close button */}
        <Button color="error" onClick={onClose}>   
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentHistoryPopup;
